import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { Actions as ALLOWANCEACTIONS } from "../../store/actions/allowanceActions";
import { Actions as EMPLOYEEACTIONS } from "../../store/actions/employeeActions";
import { Actions as SCHEME_ACTIONS } from "../../store/actions/schemeActions";

import { addMonths, format } from "date-fns";
import _ from "lodash";

import { useLocalStorage } from "@/components/common/helpers/ReactHooks";
import IntegrationBulkLink from "@/components/integrations/IntegrationBulkLink";
import IntegrationBulkLinkModal from "@/components/integrations/IntegrationBulkLinkModal";
import { CalendarIcon, EyeIcon, EyeSlashIcon } from "@/icons/outline";
import Tooltip from "@/tooltip";
import { MagnifyingGlassIcon, PlusIcon } from "@heroicons/react/20/solid";
import DatePicker from "react-datepicker";
import DeleteConfirmationModal from "../admin/modals/DeleteConfirmationModal";
import LoadingContent from "../common/LoadingContent";
import { UserPlusIcon } from "../common/icons/Solid";
import ArchivedCompany from "../company-pages/ArchivedCompany";
import InvalidPermissions from "../company-pages/InvalidPermissions";
import IntegrationImportEmployeeModal from "../integrations/IntegrationImportEmployeeModal";
import EmployeeRow from "./EmployeeRow";
import ImportEmployeeButton from "./ImportEmployeeButton";
import COLAEmployeeModal from "./modals/cola-modal/COLAEmployeeModal";
import ImportEmployeeModal from "./modals/import-employee-modal/ImportEmployeeModal";
import EmployeeFilter from "./EmployeeFilter";

const DateInputFormat = React.forwardRef(
	(
		{
			value,
			onChange,
			onClick,
			onFocus,
			onKeyDown,
			id,
			name,
			autoFocus,
			placeholder,
			disabled,
			autoComplete,
			className,
			readOnly,
			required,
			tabIndex,
		},
		ref,
	) => {
		return (
			<div className="input-group input-date date">
				<input
					ref={ref}
					value={value}
					onChange={onChange}
					onFocus={onFocus}
					onKeyDown={onKeyDown}
					className={className}
					name={name}
					style={{ border: "none", width: 80, padding: "10px 0", lineHeight: "20px" }}
				/>
			</div>
		);
	},
);

const Employees = ({
	employeeListWithSalary,
	employeesGetListWithSalary,
	userPermissions,
	setNewEmployeeModal,
	orgId,
	showInviteEmployeeModal,
	allowanceGetList,
	clearEmployee,
	companyStatus,
	setUpStatus,
	hasDepartments,
	cisEnabled,
}) => {
	const [search, setSearch] = useState("");
	const [salaryVisibility, setSalaryVisibility] = useLocalStorage("salaryVisibility", true);
	const [fromDate, setFromDate] = useState(new Date(format(addMonths(new Date(), -2), "yyyy-MM-01")));
	const [toDate, setToDate] = useState(new Date(format(new Date(), "yyyy-12-31")));
	const [filteredEmployeeList, setFilteredEmployeeList] = useState(employeeListWithSalary);

	useEffect(() => {
		employeesGetListWithSalary({ from: format(fromDate, "yyyy-MM-dd"), to: format(toDate, "yyyy-MM-dd") });
		allowanceGetList();
	}, [orgId]);

	useEffect(() => {
		clearEmployee();
	}, []);

	useMemo(() => {
		const filterByValue = (o) =>
			[
				"name",
				"employeeName",
				"employeeSurname",
				"employmentTypeName",
				"roleName",
				"departmentName",
				"idNo",
				"socialSecurity",
				"reference",
				"status",
			].some((k) => String(o[k])?.toLowerCase().includes(search.toLowerCase()));

		setFilteredEmployeeList(search ? filteredEmployeeList?.filter(filterByValue) : employeeListWithSalary);
	}, [search, employeeListWithSalary]);

	if (companyStatus == "Archived") return <ArchivedCompany />;
	if (userPermissions == "None") return <InvalidPermissions />;

	return (
		<>
			{filteredEmployeeList ? (
				<>
					<DeleteConfirmationModal />
					<COLAEmployeeModal />
					<div className="content bg-gray-lighter">
						<div className="row">
							<div className="col-sm-12">
								<div className="pull-left">
									<h1 className="page-heading">People</h1>
								</div>
								{userPermissions != "Read" && (
									<div className="pull-right">
										{
											cisEnabled &&
											<div
												className="btn btn-success mr-4"
												onClick={() => setNewEmployeeModal("cis")}
											>
												<PlusIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
												<div style={{ float: "left" }}>Add New CIS Contractor</div>
											</div>
										}
										<div
											className="btn btn-success"
											onClick={() => setNewEmployeeModal("default")}
										>
											<PlusIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
											<div style={{ float: "left" }}>Add New Employee</div>
										</div>
										<button
											className="btn btn-success pull-right ml-4"
											onClick={() => {
												showInviteEmployeeModal();
											}}
											disabled={companyStatus == "Setup"}
										>
											<Tooltip
												content={"Cannot invite Employees during Setup"}
												isOpen={setUpStatus}
												arrowSize={4}
												distance={10}
											>
												<UserPlusIcon
													style={{ height: "1.5em", float: "left", marginRight: 5 }}
												/>
												<div style={{ float: "left" }}>Invite Employees</div>
											</Tooltip>
										</button>
									</div>
								)}
							</div>
						</div>
					</div>
					<div className="content mb-32">
						<div id="employees">
							<div
								style={{
									border: "1px solid #bbbcbc",
									borderBottom: "none",
									position: "relative",
									height: 40,
								}}
							>
								<CalendarIcon
									className={"text-gray"}
									style={{
										height: "1.5em",
										float: "left",
										margin: "0.75em",
									}}
								></CalendarIcon>
								<div
									style={{
										lineHeight: "17px",
										display: "inline-block",
									}}
								>
									<DatePicker
										selected={fromDate}
										onChange={(newFromDate) => {
											setFromDate(newFromDate);
											employeesGetListWithSalary({
												from: format(newFromDate, "yyyy-MM-dd"),
												to: format(toDate, "yyyy-MM-dd"),
											});
										}}
										dateFormat="do MMM yyyy"
										customInput={<DateInputFormat />}
									/>
									<span
										style={{
											padding: "10px",
											lineHeight: "20px",
											verticalAlign: "top",
											display: "inline-block",
										}}
									>
										—
									</span>
									<DatePicker
										selected={toDate}
										onChange={(newToDate) => {
											setToDate(newToDate);
											employeesGetListWithSalary({
												from: format(fromDate, "yyyy-MM-dd"),
												to: format(newToDate, "yyyy-MM-dd"),
											});
										}}
										dateFormat="do MMM yyyy"
										customInput={<DateInputFormat />}
									/>
								</div>
								{/*
              // divider
              <div style={{
                marginLeft: 10, 
                display:'inline-block', 
                width: 1, 
                height: 40, 
                borderRight: "1px solid #bbbcbc",
                display: 'none', 
              }}></div>
            */}
							</div>
							<div
								style={{
									border: "1px solid #bbbcbc",
									borderBottom: "none",
									position: "relative",
								}}
							>
								<MagnifyingGlassIcon
									className={"text-gray"}
									style={{
										height: "1.5em",
										position: "absolute",
										margin: "0.75em",
									}}
								></MagnifyingGlassIcon>
								<input
									name="search"
									onChange={(e) => setSearch(e.target.value)}
									value={search}
									style={{
										border: "none",
										display: "inline-block",
										maxWidth: "100%",
										width: "100%",
										padding: 10,
										paddingLeft: 40,
									}}
								/>

								<EmployeeFilter
									employees={filteredEmployeeList}
									setEmployees={setFilteredEmployeeList}
								/>
							</div>

							<div
								className="table-responsive"
								style={{ overflow: "auto" }}
							>
								{/* If it is already loaded this */}
								<div
									id="active-people-table_wrapper"
									className="dataTables_wrapper no-footer"
								>
									<table
										id="active-people-table"
										className="js-table-sections table table-hover"
									>
										<thead style={{ display: "table-header-group" }}>
											<tr>
												<th width="5%"></th>
												<th>NAME</th>
												<th>D.O.E</th>
												<th>EMPLOYMENT TYPE</th>
												{hasDepartments ? <th>DEPARTMENT</th> : null}
												<th>ROLE</th>
												<th>
													<div
														style={{ cursor: "pointer", display: "inline-block" }}
														onClick={(e) => {
															e.preventDefault();
															setSalaryVisibility(!salaryVisibility);
														}}
													>
														SALARY
														{salaryVisibility ? (
															<EyeIcon className="ml-2 h-7 w-7 align-middle" />
														) : (
															<EyeSlashIcon className="ml-2 h-7 w-7 align-middle" />
														)}
													</div>
												</th>
												<th></th>
											</tr>
										</thead>
										<tbody>
											{filteredEmployeeList.map((employee) => {
												return (
													<EmployeeRow
														key={employee.id}
														employee={employee}
														salaryVisibility={salaryVisibility}
													/>
												);
											})}
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>
					<div className="pull-right">
						<div
							className="btn-group"
							style={{ marginRight: 30 }}
						>
							<ImportEmployeeButton />
							<IntegrationBulkLink />
						</div>
					</div>

					<ImportEmployeeModal />
					<IntegrationImportEmployeeModal />
					<IntegrationBulkLinkModal />
				</>
			) : (
				<LoadingContent />
			)}
		</>
	);
};

const mapStateToProps = (state) => {
	//All the company's employee
	const employeeListWithSalary =
		state?.employees?.employeeListWithSalary &&
		_.orderBy(
			state.employees.employeeListWithSalary.map((e) => ({
				...e,
				name: `${e.employeeName?.trim?.()} ${e.employeeSurname?.trim?.()}`,
				status: e.incomplete ? "Incomplete" : e.isTerminated ? "Terminated" : "Currently Employed",
			})),
			["departmentName", "employeeName", "employeeSurname"],
			["asc", "asc", "asc"],
		);

	const employeeOptions = employeeListWithSalary?.map((employee) => ({
		value: employee.id,
		label: `${employee.employeeName} ${employee.employeeSurname}`,
		jobType: employee.employmentTypeName,
	}));

	const { status, enableDepartments, departments } = state?.company?.settings;

	const userPermissions = state?.permissions?.my?.permissions?.people;

	return {
		pensions: state?.pensions?.group?.list,
		userPermissions,
		employeeOptions,
		employeeListWithSalary,
		orgId: state?.company?.settings?.org,
		companyStatus: status,
		hasDepartments: enableDepartments === undefined ? !!departments?.length : enableDepartments,
		setUpStatus: status === "Setup" ? undefined : false,
		cisEnabled: state?.company?.settings?.enable?.cis,
	};
};

const mapDispatchToProps = (dispatch) => ({
	getPensionList: () => dispatch(SCHEME_ACTIONS.getSchemes()),
	allowanceGetList: () => dispatch(ALLOWANCEACTIONS.allowanceGetList()),
	openImportModal: () => dispatch(EMPLOYEEACTIONS.toggleImportModal()),
	setNewEmployeeModal: (type) => dispatch(EMPLOYEEACTIONS.setNewEmployeeModal(true,false, {type})),
	showInviteEmployeeModal: () => dispatch(EMPLOYEEACTIONS.showInviteEmployeeModal(true)),
	clearEmployee: () => dispatch(EMPLOYEEACTIONS.clearEmployee()),
	employeesGetListWithSalary: ({ from, to }) =>
		dispatch(
			EMPLOYEEACTIONS.employeesGetListWithSalary(
				{
					from: from ? from : format(addMonths(new Date(), -2), "yyyy-MM-01"), // get list from last 2 months
					to: to ? to : format(new Date(), "yyyy-12-31"),
				},
				{ includeTax: true },
			),
		),
	toggleSalaryVisibility: (salaryVisibility) => dispatch(EMPLOYEEACTIONS.toggleSalaryVisibility(salaryVisibility)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Employees);
